import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import TeamMemberForm from '../TeamMemberForm';
import MeetingArrangement from '../MeetingArrangement';
import InstructionFormLayout from './InstructionFormLayout';
import PersonalParticularsFormLayout from './SiteInfo/PersonalParticularsFormLayout';
import OutlineOfApplicationFormLayout from './OutlineOfApplicationFormLayout';
import StudyDetailsFormLayout from './StudyDetailsFormLayout';
import StudyDetailForProspectiveStudyFormLayout from './StudyDetailForProspectiveStudyFormLayout';
import EthicalImplicationsFormLayout from './EthicalImplicationsFormLayout';
import BudgetFormLayout from './BudgetFormLayout';
import DocumentUploadFormLayout from './DocumentUploadFormLayout';
import ClinicalStudyCategorizationFormLayout from './ClinicalStudyCategorizationFormLayout';
import DeclarationAndEndorsementFormLayout from './DeclarationAndEndorsementFormLayout';
import useQuery from 'hooks/useQuery';
import AmendmentForm from '../Amendment';
import CommentSheet from 'components/CommentSheet';
import CustomTab from 'components/CustomInput/Tab';
import {
    amendmentClearMessage,
    checkCIAmendment,
    generateRandomId,
    hasAmendmentDetails,
    isFormChanged,
    permissionChecking,
    publish,
} from 'utility/general';
import Navigation from './Navigation';
import ApplicationActionBar from './ApplicationActionBar';
import AmendmentBar from './AmendmentBar';
import {
    APPLICATION_DETAIL_FETCH,
    CLEAR_FORM_VALIDATION,
    RESET_APPLICATION_FORM_INITIAL_POST_DATA,
    SET_APPLICATION_DETAIL_VALID,
    UPDATE_APPLICATION_DETAIL_POST_DATA,
    UPDATE_APPLICATION_SITE_DETAIL_POST_DATA,
    RESET_CLINICAL_STUDY_CATEGORIZATION,
    RESET_DOCUMENT_UPLOAD,
    RESET_DECLARATION_AND_ENDORSEMENT,
    SAVE_REMOTE_APPLICATION_DETAIL,
    CLEAR_TEXT_VALIDATION,
    CLEAR_APPLICATION_DETAIL,
    CHECK_CONSOLIDATION_UPDATE,
    RESET_CONSOLIDATION,
    GET_ALL_COMMENT,
    SET_CURRENT_TAB_ID,
    LOAD_ALL_CONFIG,
} from 'store/key';
import LoadingIndicator from 'components/LoadingIndicator';
import { checkFormValid, getSiteInfoValidationObject } from 'utility/formValidate';
import { useLocation } from 'react-router';
import usePrevious from 'hooks/usePrevious';
import { setGlobal, setGlobalLoading } from 'store/global/reducer';
import useDialog from 'components/ConfirmDialog/useDialog';
import {
    APPLICATION_CHECK_FORM_COMPLETENESS,
    CHANGES_DETECTED,
    COMPARE_CHANGE_TIPS,
    COMPARE_CHANGE_REMINDER,
    CONFIRM_TO_LEAVE_AMENDMENT,
    SAVE_BEFORE_NAVIGATION,
} from 'utility/frontendMessage';

import {
    APPLICANT,
    CCO,
    CLUSTER_ADMIN,
    DRAFT,
    IRB,
    RETURNED,
    SECRETARY,
    APPROVED,
    ACKNOWLEDGED,
    DISAPPROVED,
    REVIEWER,
    FOR_REVIEW,
    COMPLETED,
    REVIEWED,
    TAB_STUDY,
    TAB_SITE,
    TAB_COMMENT,
    TAB_AMENDMENT,
    TAB_REVIEWER_COMMENT,
    TAB_CONSOLIDATION,
    TAB_MEETINGARRANGE,
    TERMINATED,
} from 'utility/lookup';
import ReviewerComment from 'components/ReviewerComment';
import ConsolidationTabPage from 'components/ConsolidationTabPage';
import { INIT_APPLICATION, AMENDMENT_FORM } from 'utility/postApprovalLookUp';
import AddComment from 'components/CommentBox/AddComment';
import CommentSummary from 'components/CommentBox/CommentSummary';
import CommentBox from 'components/CommentBox';
import api from 'api/comment';

const SLayout = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const SContentLayout = styled.div`
    flex-grow: 1;
    display: flex;
    height: 100%;
`;

const SActionBar = styled.div`
    background: #ecebf3;
    height: 55px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    justify-content: space-between;
`;

const SBottomTableLayout = styled.div`
    position: absolute;
    bottom: 0px;
    left: 0;
    right: 0;
    height: 35px;
    flex-shrink: 0;
`;

const SContent = styled.div`
    flex-grow: 1;
    display: flex;
    width: 100%;
    height: 100%;
    position: relative;
`;

const SContentAbsolute = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 35px;
    overflow-y: auto;
`;

const SLoadingLayout = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const SNavigationFooterButton = styled.div`
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 4px;
    padding-bottom: 4px;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    display: flex;
    justify-content: center;
    align-items: center;
`

const SNavigationFooterButtonText = styled.div`
    margin-top: 4px;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
`

const InitialApplication = ({ name }) => {
    const formId = useQuery('id');
    const amendmentType = useQuery('type');
    const amendId = useQuery('typeId');
    const applicationDetail = useSelector((state) => state.applicationDetail[formId ?? 'undefined']);
    const isLoading = useSelector((state) => state.applicationDetail.isLoading);
    const previousLoading = usePrevious(isLoading);
    const isError = useSelector((state) => state.applicationDetail.isError);
    const isShowInHospitalBase = useSelector((state) => state.global.isShowInHospitalBase);
    const compareChangeKey = useSelector((state) => state.global.compareChangeKey);
    const dispatch = useDispatch();
    const [currentSiteInfoId, setCurrentSiteInfoId] = useState(-1);
    const isSiteInfoInitialized = useRef(false);
    const location = useLocation();
    const showDialog = useDialog();
    const isAmendmentCreate = location.pathname.startsWith('/applications/create-application-amendment');
    const isAmendmentEdit = location.pathname.startsWith('/post-approval-document/application-amendment-detail');
    const isAmendment = isAmendmentEdit || isAmendmentCreate;
    const [hasAmendmentChange, setAmendmentChange] = useState(false);
    const user = useSelector((state) => state.user);
    const [currentTab, setCurrentTab] = useState({});
    const [initialize, setInitialize] = useState(false);
    const [amendInstitution, setAmendInstitution] = useState('');

    const [prevTabType, setPrevTabType] = useState(null);

    const documentType = isAmendment ? AMENDMENT_FORM : INIT_APPLICATION;

    const applicationForm = applicationDetail?.applicationForm ?? {};
    const workflow = applicationForm?.workflow;

    const { allComments } = useSelector(state => state.comment);
    const getCommentDetail = (fieldName, comments) => {
        if(!Array.isArray(comments)) return null;
        return comments.find(item => item.questionNo === fieldName)
    }

    const updateSiteInfoValidation = () => {
        const { siteInfo } = applicationDetail;
        const finalResult = {};

        siteInfo.forEach((item) => {
            const updateObject = getSiteInfoValidationObject(applicationDetail, item, formId);
            finalResult[item.id] = updateObject;
        });

        dispatch({
            type: SET_APPLICATION_DETAIL_VALID,
            payload: { siteInfoValid: finalResult },
        });
    };

    useEffect(() => {
        if (applicationDetail) {
            setInitialize(true);
        }
        if (applicationDetail?.applicationForm?.amendInstitution) {
            setAmendInstitution(applicationDetail?.applicationForm?.amendInstitution);
        }
    }, [applicationDetail]);


    useEffect(() => {
        dispatch({ type: LOAD_ALL_CONFIG })
    }, [])

    // initialize site-info validation data
    useEffect(() => {
        // when application form detail has been loaded, update the site info validation
        if (!isLoading && previousLoading) {
            if (!isSiteInfoInitialized.current && applicationDetail) {
                isSiteInfoInitialized.current = true;
                updateSiteInfoValidation();
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationDetail, isLoading, previousLoading]);

    // setting current tab
    useEffect(() => {
        if (initialize) {
            if (isAmendmentEdit) {
                const role = user.role;
                const applicationTabs = getApplicationTabs();

                if (role === CCO || role === CLUSTER_ADMIN || role === IRB || role === SECRETARY) {
                    const index = applicationTabs.findIndex((item) => item.key === TAB_AMENDMENT);

                    if (index === -1) {
                        setCurrentTab(getApplicationTabs()[0]);
                    } else {
                        setCurrentTab(applicationTabs[index]);
                    }
                } else {
                    setCurrentTab(getApplicationTabs()[0]);
                }
            } else {
                setCurrentTab(getApplicationTabs()[0]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialize]);

    useEffect(() => {
        if (compareChangeKey !== 'initialize') {
            setPrevTabType(TAB_AMENDMENT);
            setCurrentTab({
                key: TAB_AMENDMENT,
                display:
                    amendInstitution !== '' ? `Amendment application (${amendInstitution})` : `Amendment application`,
                type: TAB_AMENDMENT,
            });
        }

        return () => {
            dispatch(
                setGlobal({
                    compareChangeKey: 'initialize',
                }),
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compareChangeKey]);

    // fetch application detail when enter the form and remove all post-data previously
    useEffect(() => {
        dispatch({ type: CLEAR_APPLICATION_DETAIL });

        dispatch({
            type: APPLICATION_DETAIL_FETCH,
            payload: {
                id: formId,
            },
        });

        dispatch({
            type: RESET_APPLICATION_FORM_INITIAL_POST_DATA,
        });

        dispatch({
            type: CLEAR_FORM_VALIDATION,
        });

        dispatch({ type: RESET_CLINICAL_STUDY_CATEGORIZATION });
        dispatch({ type: RESET_DOCUMENT_UPLOAD });
        dispatch({ type: RESET_DECLARATION_AND_ENDORSEMENT });
        dispatch({ type: CLEAR_TEXT_VALIDATION });
        dispatch({ type: RESET_CONSOLIDATION });
        
        dispatch({ type: GET_ALL_COMMENT, payload: formId });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId]);

    useEffect(() => {
        dispatch({ type: SET_CURRENT_TAB_ID, payload: currentSiteInfoId })
    }, [currentSiteInfoId])

    const [listOfSection] = useState([
        {
            text: 'Instruction',
            ref: useRef(),
            className: generateRandomId(),
            component: InstructionFormLayout,
            showInTab: [TAB_STUDY],
            disableForRoles: [CCO, CLUSTER_ADMIN],
            // showInHospitalBase: false,
        },
        {
            text: 'Team Member Form',
            ref: useRef(),
            className: generateRandomId(),
            component: TeamMemberForm,
            showInTab: [TAB_STUDY, TAB_SITE],
            disableForRoles: [CCO, CLUSTER_ADMIN],
            // showInHospitalBase: true,
        },
        {
            text: 'Personal Particulars',
            ref: useRef(),
            className: generateRandomId(),
            component: PersonalParticularsFormLayout,
            showInTab: [TAB_SITE],
            showInHospitalBase: true,
        },
        {
            text: 'PART I: OUTLINE OF APPLICATION',
            ref: useRef(),
            className: generateRandomId(),
            component: OutlineOfApplicationFormLayout,
            showInTab: [TAB_STUDY, TAB_SITE],
            showInHospitalBase: true,
        },
        {
            text: 'PART IIA: STUDY DETAILS',
            ref: useRef(),
            className: generateRandomId(),
            component: StudyDetailsFormLayout,
            showInTab: [TAB_STUDY, TAB_SITE],
            showInHospitalBase: true,
        },
        {
            text: 'PART IIB: STUDY DETAILS FOR PROSPECTIVE STUDY',
            ref: useRef(),
            className: generateRandomId(),
            component: StudyDetailForProspectiveStudyFormLayout,
            showInTab: [TAB_STUDY, TAB_SITE],
            showInHospitalBase: true,
        },
        {
            text: 'PART III: ETHICAL IMPLICATIONS',
            ref: useRef(),
            className: generateRandomId(),
            component: EthicalImplicationsFormLayout,
            showInTab: [TAB_STUDY, TAB_SITE],
            showInHospitalBase: false,
        },
        {
            text: 'PART IV: BUDGET, RESOURCES, INDEMNITY AND INSURANCE',
            ref: useRef(),
            className: generateRandomId(),
            component: BudgetFormLayout,
            showInTab: [TAB_STUDY, TAB_SITE],
            showInHospitalBase: true,
        },
        {
            text: 'CLINICAL STUDY CATEGORIZATION FORM',
            ref: useRef(),
            className: generateRandomId(),
            component: ClinicalStudyCategorizationFormLayout,
            showInTab: [TAB_STUDY],
            showInHospitalBase: false,
        },
        {
            text: 'Document Upload ',
            ref: useRef(),
            className: generateRandomId(),
            component: DocumentUploadFormLayout,
            showInTab: [TAB_STUDY, TAB_SITE],
            showInHospitalBase: true,
            disableForRoles: [CCO, CLUSTER_ADMIN],
        },
        {
            text: 'Declaration And Endorsement',
            ref: useRef(),
            className: generateRandomId(),
            component: DeclarationAndEndorsementFormLayout,
            showInTab: [TAB_SITE],
            showInHospitalBase: false,
            disableForRoles: [CCO, CLUSTER_ADMIN],
        },
    ]);

    const onFormUpdate = (name, data) => {
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: 'applicationForm',
                key: name,
                data,
                applicationFormId: formId,
            },
        });
    };

    const onSiteFormUpdate = (siteInfoId, name, data) => {
        dispatch({
            type: UPDATE_APPLICATION_SITE_DETAIL_POST_DATA,
            payload: {
                key: name,
                data,
                siteInfoId,
                applicationFormId: formId,
            },
        });
    };

    const onTableUpdate = (tableKey, tableData) => {
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: tableKey,
                data: tableData,
                applicationFormId: formId,
            },
        });
    };

    const shouldDrawerShow = () => {
        const type = currentTab.type;
        return type === TAB_SITE || type === TAB_STUDY;
    };

    const getHospitalBaseListOfSection = (isShowInHospitalBase) => {
        return listOfSection.filter((item) => {
            return item.showInTab.includes(currentTab.type);
        });
    };

    const getContent = () => {
        const type = currentTab.type;

        if (type === TAB_AMENDMENT) {
            return (
                <AmendmentForm
                    updateHasChange={(hasChange) => {
                        setAmendmentChange(hasChange);
                    }}
                    applicationDetail={applicationDetail}
                    backToStudyInfo={() => {
                        setCurrentTab(getApplicationTabs()[0]);
                    }}
                />
            );
        } else if (type === TAB_COMMENT) {
            return <CommentSheet applicationDetail={applicationDetail} />;
        } else if (type === TAB_REVIEWER_COMMENT) {
            return <ReviewerComment formId={formId} documentType={documentType} />;
        } else if (type === TAB_CONSOLIDATION) {
            const isPermission = permissionChecking({ applicationDetail, tabType: type });
            return <ConsolidationTabPage formId={formId} isPermission={isPermission} documentType={documentType} />;
        } else if (type === TAB_MEETINGARRANGE) {
            const isPermission = permissionChecking({ applicationDetail, tabType: type });
            const meetingCase = applicationDetail?.arrangementFormIdList;
            return <MeetingArrangement formId={formId} documentType={documentType} meetingCase={meetingCase} />;
        } else {
            const isEditPermit = permissionChecking({
                tabType: type,
                siteInfoId: currentSiteInfoId,
                applicationDetail,
                isAmendmentCreate,
                isAmendmentEdit,
                amendmentType,
                amendId,
            });

            const isCommentEnabled = (type === TAB_STUDY || type === TAB_SITE) && isEditPermit;
            const isTableOperationDisabled = user.role === CCO || user.role === CLUSTER_ADMIN;

            return (
                <>
                    <AddComment />
                    <CommentSummary 
                        tabs={getApplicationTabs()}
                        defaultTab={currentTab}
                        formId={formId}
                    />
                    <CommentBox isEnableComment={isCommentEnabled} />
                    <div key={`${formId}-${currentSiteInfoId}`}>
                        {getHospitalBaseListOfSection(isShowInHospitalBase).map((item, index) => {
                            const Section = item.component;
                            const disableSection = item.disableForRoles?.includes(user.role) ?? false;
                            
                            return (
                                <div ref={item.ref} className={item.className} key={item.text}>
                                    <div className={'clearText dark-placeholder'}>
                                        <Section
                                            status = {applicationDetail?.status?.statusId}
                                            isAmendment = {isAmendment}
                                            formId={formId}
                                            applicationDetail={applicationDetail}
                                            applicationCreateDtm={applicationDetail.applicationForm.createDtm}
                                            tabType={type}
                                            disableOnPermission={!isEditPermit || disableSection}
                                            disableTableOperations={isTableOperationDisabled}
                                            onFormUpdate={onFormUpdate}
                                            defaultData={applicationDetail.applicationForm}
                                            onTableUpdate={onTableUpdate}
                                            onSiteInfoUpdate={onSiteFormUpdate}
                                            siteInfoId={currentSiteInfoId}
                                            isAmendmentCreate={isAmendmentCreate}
                                            isAmendmentEdit={isAmendmentEdit}
                                            isLoading={isLoading}
                                            previousLoading={previousLoading}
                                            isEnableComment={isCommentEnabled}
                                            allComments={allComments}
                                            getCommentDetail={getCommentDetail}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            );
        }
    };

    const hasUnderAmendment = () => {
        if (isAmendmentCreate) {
            return false;
        }

        // if isAmendment is false in the form, the application must not an amendment form
        if (!applicationDetail?.applicationForm?.isAmendment) {
            return false;
        }

        const isStudyAmendment = applicationDetail?.applicationForm?.underAmendment;

        // disable amendment tab if user is new site pi in draft or returned status
        if (isStudyAmendment) {
            const appStatus = applicationDetail.status.statusId;

            if (appStatus === DRAFT) {
                const isCI = checkCIAmendment(applicationDetail, user.detail);

                if (!isCI) {
                    return false;
                }
            } else if (appStatus === RETURNED) {
                return true;
            }
        }

        return true;
    };

    const getApplicationTabs = () => {
        const applicationTabs = [{ key: 'studyInfo', display: 'Study Info', type: TAB_STUDY }];

        if (applicationDetail?.siteInfo) {
            applicationDetail.pi.forEach((item) => {
                if (item.endEffectiveDate) {
                    applicationTabs.push({
                        key: generateRandomId(),
                        display: `${item.institution}`,
                        type: TAB_SITE,
                        disabled: true,
                    });
                } else {
                    applicationTabs.push({
                        key: item.siteInfoId,
                        display: item.institution,
                        type: TAB_SITE,
                        disabled: false,
                    });
                }
            });
        }

        // amendment tab show logic
        let isAmendmentShow = hasUnderAmendment();

        if (isAmendmentShow) {
            applicationTabs.push({
                key: TAB_AMENDMENT,
                display:
                    amendInstitution !== '' ? `Amendment application (${amendInstitution})` : `Amendment application`,
                type: TAB_AMENDMENT,
            });
        }

        /** statusId > 0 to show comment sheet && not amendment form && user.role !== REVIEWER*/
        if (applicationDetail?.status?.statusId > 0 && !isAmendmentCreate && user.role !== REVIEWER) {
            applicationTabs.push({ key: TAB_COMMENT, display: 'Comment Sheet', type: TAB_COMMENT });
        }

        /** This tab will be shown in IRB / Secretary / Reviewer when CCO / Secretary confirms the document in confirmed, for review, reviewed, approved, acknowledged, disapproved, completed status.  */
        if (
            [FOR_REVIEW, REVIEWED, APPROVED, ACKNOWLEDGED, DISAPPROVED, COMPLETED].includes(
                applicationDetail?.status?.statusId,
            ) &&
            [SECRETARY, IRB, REVIEWER].includes(user.role)
        ) {
            applicationTabs.push({
                key: TAB_REVIEWER_COMMENT,
                display: 'Reviewer Comment',
                type: TAB_REVIEWER_COMMENT,
            });
        }

        const showConsolidate = [
            FOR_REVIEW,
            TERMINATED,
            COMPLETED,
            REVIEWED,
            ACKNOWLEDGED,
            DISAPPROVED,
            APPROVED,
        ].includes(applicationDetail?.status?.statusId);
        if (showConsolidate && [SECRETARY, IRB].includes(user.role)) {
            applicationTabs.push({ key: TAB_CONSOLIDATION, display: 'Consolidation', type: TAB_CONSOLIDATION });
        }

        const showMeetingArrange = [
            FOR_REVIEW,
            TERMINATED,
            COMPLETED,
            REVIEWED,
            ACKNOWLEDGED,
            DISAPPROVED,
            APPROVED,
        ].includes(applicationDetail?.status?.statusId);
        const meetingCase = applicationDetail?.arrangementFormIdList;
        if (showMeetingArrange && [SECRETARY, IRB].includes(user.role) && meetingCase?.[0] !== undefined) {
            applicationTabs.push({ key: TAB_MEETINGARRANGE, display: 'Meeting Arrangement', type: TAB_MEETINGARRANGE });
        }

        return applicationTabs;
    };

    const isFormTypeMatch = () => {
        return applicationDetail?.applicationForm?.isAmendment === isAmendment;
    };

    const getApplicationButtonBar = () => {
        if (!applicationDetail) {
            return null;
        }

        if (!isFormTypeMatch()) {
            return null;
        }

        if (isAmendment) {
            // const status = applicationDetail.status.statusId;
            const isAmendmentTab = currentTab.type === TAB_AMENDMENT;

            if (user.role === APPLICANT && isAmendmentTab) {
                return null;
            }

            return (
                <SActionBar>
                    <AmendmentBar
                        backToStudyInfo={() => {
                            setCurrentTab(getApplicationTabs()[0]);
                        }}
                        amendmentType={amendmentType}
                        amendId={amendId}
                        isAmendmentEdit={isAmendmentEdit}
                        isAmendmentCreate={isAmendmentCreate}
                        applicationDetail={applicationDetail}
                        currentTab={currentTab}
                        siteInfoId={currentSiteInfoId}
                        documentType={documentType}
                    />
                </SActionBar>
            );
        } else {
            return (
                <SActionBar>
                    <ApplicationActionBar
                        currentTab={currentTab}
                        siteInfoId={currentSiteInfoId}
                        applicationFormId={formId}
                        backToStudyInfo={() => {
                            setCurrentTab(getApplicationTabs()[0]);
                        }}
                        documentType={documentType}
                    />
                </SActionBar>
            );
        }
    };

    const tabNavigate = (tab) => {
        const contentContainer = document.getElementById('content-container');
        contentContainer.scrollTop = 0;

        setCurrentTab(tab);

        if (tab.key !== 'studyInfo') {
            setCurrentSiteInfoId(tab.key);
        } else {
            setCurrentSiteInfoId(-1);
        }
    };

    if (!currentTab) {
        return null;
    }

    const changeTab = (value) => {
        tabNavigate(value);
        setPrevTabType(value.type);
    };

    const handleTabButton = async (value) => {
        if (prevTabType === TAB_AMENDMENT) {
            if (hasAmendmentChange) {
                const isConfirm = await showDialog({
                    description: CONFIRM_TO_LEAVE_AMENDMENT,
                });

                // prevent leave amendment when there is amendment form changes
                if (!isConfirm) {
                    return;
                }
            }
        }

        if (prevTabType === TAB_CONSOLIDATION) {
            dispatch({
                type: CHECK_CONSOLIDATION_UPDATE,
                payload: {
                    formId,
                    documentType,
                    showDialog,
                    callback: async (isSaved) => {
                        if (isSaved) {
                            await showDialog({
                                description: 'Save success',
                                disableCancelButton: true,
                            });
                            changeTab(value);
                        } else {
                            changeTab(value);
                        }
                    },
                },
            });
            return;
        }

        if (value.key === TAB_AMENDMENT) {
            const isChange = await isFormChanged();
            if (!isChange) {
                setCurrentTab(value);
                setPrevTabType(value.type);
            } else {
                // validation will only work on one tab only
                const isStudyAmendment = applicationDetail?.applicationForm?.underAmendment;
                const isSiteAmendment = !isStudyAmendment;
                let siteToAmend = '';

                if (isSiteAmendment) {
                    const id = applicationDetail.siteInfo.find((item) => item.underAmendment)?.id;
                    siteToAmend = id;
                }

                const result = checkFormValid(applicationDetail, isStudyAmendment, isSiteAmendment, siteToAmend);

                if (!result.isFormValid) {
                    return showDialog({
                        title: APPLICATION_CHECK_FORM_COMPLETENESS,
                        disableCancelButton: true,
                        messages: result.messages,
                    });
                }

                // setGlobalLoading(true);

                // const hasAmendmentBefore = await hasAmendmentDetails(formId);

                // setGlobalLoading(false);

                // if (hasAmendmentBefore) {
                //     const messages = [];
                //     messages.push(amendmentClearMessage);
                //     const confirm = await showDialog({
                //         description: COMPARE_CHANGE_TIPS,
                //         messages,
                //     });

                //     if (!confirm) {
                //         return;
                //     }
                // } else {
                //     const confirm = await showDialog({
                //         description: CHANGES_DETECTED,
                //     });

                //     if (!confirm) {
                //         return;
                //     }
                // }

                const confirm = await showDialog({
                    description: COMPARE_CHANGE_REMINDER,
                });
                if (!confirm) {
                    return;
                }

                setGlobalLoading(true);
                dispatch({
                    type: SAVE_REMOTE_APPLICATION_DETAIL,
                    payload: {
                        id: formId,
                        tabType: currentTab.type,
                        callback: async () => {
                            setCurrentTab(value);
                            setPrevTabType(value.type);
                        },
                        final: () => {
                            setGlobalLoading(false);
                        },
                    },
                });
            }
        } else {
            const isFormUpdate = await isFormChanged();
            let isRedirect = true;

            if (user.role === APPLICANT) {
                if (isFormUpdate) {
                    isRedirect = await showDialog({
                        description: SAVE_BEFORE_NAVIGATION,
                    });

                    if (isRedirect) {
                        publish({
                            topic: isAmendment ? 'saveAmendment' : 'saveApplication',
                            args: {
                                disableSaveConfirm: true,
                            },
                            callback: async () => {
                                await showDialog({
                                    description: 'Save success',
                                    disableCancelButton: true,
                                });

                                changeTab(value);
                            },
                        });
                    }
                } else {
                    changeTab(value);
                }
            } else {
                changeTab(value);
            }
        }
    };
    return (
        <SLayout>
            {getApplicationButtonBar()}
            <SContentLayout>
                {isFormTypeMatch() && shouldDrawerShow() && !isLoading && !isError && (
                    <Navigation 
                        listOfSections={getHospitalBaseListOfSection(isShowInHospitalBase)} 
                        footer={workflow === 'I' || workflow === 'H' ? (
                            <SNavigationFooterButton>
                                <SNavigationFooterButtonText>
                                    {/*TODO PDF for test*/}
                                    {/* <a href='/files/test.pdf' download="test.pdf">
                                      Download PDF  
                                    </a> */}
                                </SNavigationFooterButtonText>
                            </SNavigationFooterButton>
                        ) : null}
                    />
                )}
                <SContent>
                    {applicationDetail ? (
                        isFormTypeMatch() ? (
                            <>
                                {isError && !isLoading && (
                                    <SLoadingLayout>
                                        Get {isAmendment ? 'Amendment Application' : 'Initial Application'} Failed!
                                    </SLoadingLayout>
                                )}
                                {isLoading ? (
                                    <SLoadingLayout>
                                        <LoadingIndicator text={'Loading application'} />
                                    </SLoadingLayout>
                                ) : (
                                    applicationDetail && (
                                        <div>
                                            <SContentAbsolute id={'content-container'}>{getContent()}</SContentAbsolute>
                                            <SBottomTableLayout>
                                                <CustomTab
                                                    data={getApplicationTabs()}
                                                    value={currentTab.key}
                                                    onClick={handleTabButton}
                                                />
                                            </SBottomTableLayout>
                                        </div>
                                    )
                                )}
                            </>
                        ) : (
                            <SLoadingLayout>Form type is not match</SLoadingLayout>
                        )
                    ) : (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <LoadingIndicator />
                        </div>
                    )}
                </SContent>
            </SContentLayout>
        </SLayout>
    );
};

export default InitialApplication;
